'use client';

import { motion, useAnimationControls } from 'motion/react';
import { useEffect } from 'react';

import { cn } from '@/utils/cn';

import { Value } from '../../types';
import { TriggerIndicator } from '../trigger-indicator';

type Props = Value & {
    currentIndex: number;
    index: number;
    onSelect: () => void;
    onSpread: () => void;
    previousIndex: number;
};

export const ValueCardDesktop = ({
    classNameColor,
    currentIndex,
    description,
    icon,
    index,
    name,
    onSelect,
    onSpread,
    previousIndex,
}: Props) => {
    const isMenuOnLeft = previousIndex > index;
    const isSelected = previousIndex === index;

    const controls = useAnimationControls();

    useEffect(() => {
        if (currentIndex > previousIndex) {
            if (index > previousIndex && index <= currentIndex) {
                const distance = index - previousIndex - 1;
                void startAnimation(distance);
            }
        } else if (
            currentIndex < previousIndex &&
            index < previousIndex &&
            index >= currentIndex
        ) {
            const distance = previousIndex - index - 1;
            void startAnimation(distance);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentIndex, index]);

    const startAnimation = async (distance: number) => {
        await controls.start({
            transition: { delay: distance * 0.1, ease: 'circInOut' },
            x: isMenuOnLeft ? -200 : 200,
        });
        onSpread();
        await controls.start({ x: 0 });
    };

    return (
        <motion.div
            animate={controls}
            key={name}
            style={{
                zIndex: isSelected ? 20 : isMenuOnLeft ? index : 10 - index,
            }}
            className={cn(
                `
                  group flex flex-1 cursor-pointer flex-col rounded-[60px] pb-16
                  pl-8 pr-10 pt-10

                  md:ml-[-200px]
                `,
                classNameColor,
            )}
            onClick={onSelect}
        >
            <div className="relative flex">
                <div
                    className={cn(
                        `
                          relative mr-14 transition-all duration-300

                          [&_svg]:size-full
                        `,
                        isMenuOnLeft
                            ? 'left-full -translate-x-full'
                            : 'left-0 translate-x-0',
                    )}
                >
                    {icon}
                </div>

                <div
                    className={cn(
                        `
                          absolute top-0 mb-10 flex h-full flex-col
                          justify-between transition-all duration-300
                        `,
                        isMenuOnLeft
                            ? 'left-0 translate-x-0'
                            : 'left-full -translate-x-full',
                    )}
                >
                    <div
                        className={`
                          absolute -mt-12 flex-1 origin-bottom-left rotate-90
                          rounded-full border p-2 text-2xl font-medium
                          text-primary-black
                        `}
                    >
                        {name}
                    </div>
                    <div />
                    <button
                        className={`
                          lg:opacity-100

                          md:opacity-0
                        `}
                    >
                        <TriggerIndicator />
                    </button>
                </div>
            </div>
            <div className="mt-10 flex flex-col gap-4 text-primary-black">
                {description}
            </div>
        </motion.div>
    );
};
